import {FormControl, FormGroup} from '@angular/forms';
import {
  DateRange,
  getEndOfDay,
  getEndOfWeek,
  getStartOfDay,
  getStartOfWeek,
  getUTCNow,
  isDateInsideRange
} from '@rsp/shared/util/util';

import moment from 'moment';
import {SelectControlOption} from '@rsp/shared/form/select.control';
import {IntervalEnum} from '@rsp/models/enum/interval.enum';
import {DateRangeDTO} from '@rsp/models/dto/date-range.dto';


export const intervalNameMapping = {
  'YEAR': 'Jahr',
  'MONTH': 'Monat',
  'WEEK': 'Woche',
  'DAY': 'Tag'
};

export class DateSelectionFormGroup extends FormGroup {
  public yearMonthOptions: SelectControlOption<DateRange>[];
  public intervalOptions: SelectControlOption[];

  private dateRanges: DateRangeDTO;

  constructor() {
    super({
      date: new FormControl({start: undefined, end: undefined}),
      interval: new FormControl(IntervalEnum.MONTH)
    });

    this.controls.date.setValue(this.getDefaultDateRange(), {emitEvent: false});

    this.yearMonthOptions = [];
    this.intervalOptions = Object.keys(IntervalEnum).map((key) => {
      return {
        option: intervalNameMapping[key],
        value: key
      };
    });

    this.intervalOptions.splice(-1, 1); // TODO Remove me
  }

  public get interval(): IntervalEnum {
    return this.controls.interval.value;
  }

  public get selectedDate(): DateRange {
    return this.controls.date.value;
  }

  public getDefaultDateRange(): DateRange {
    const now = getUTCNow();
    return {
      start: new Date(now.getFullYear(), 9, 1),
      end: new Date(now.getFullYear() + 1, 0, 0)
    };
  }

  public init(ranges: DateRangeDTO) {
    this.controls.interval.valueChanges.subscribe((val) => {
      const oldDateRange: DateRange = this.controls.date.value;
      this.yearMonthOptions = this.calcDateOptions(val);
      this.controls.date.setValue(this.findClosestMatch(oldDateRange), {emitEvent: false});
    });

    if (!ranges) {
      return;
    }

    this.setRanges(ranges);

    this.yearMonthOptions = this.calcDateOptions(this.value.interval);
    this.controls.date.setValue(this.findEarliestDate(), {emitEvent: false});

    this.initControls();

  }


  private initControls() {
    const nowOption = this.findOption(getUTCNow());

    if (nowOption) {
      this.controls.date.setValue({start: nowOption.start, end: nowOption.end}, {emitEvent: true});
    } else {
      const dateRange: DateRange = this.findEarliestDate();
      const emitValue = !!dateRange;
      this.controls.date.setValue(dateRange, {emitEvent: emitValue});
    }
  }

  private calcDateOptions(value: IntervalEnum): SelectControlOption<DateRange>[] {
    switch (value) {
      case IntervalEnum.YEAR:
        return this.calcYearOptions(this.dateRanges);

      case IntervalEnum.MONTH:
        return this.calcMonthOptions(this.dateRanges);

      case IntervalEnum.WEEK:
        return this.calcWeekOptions(this.dateRanges);
    }
  }

  private setRanges(ranges: DateRangeDTO) {
    this.dateRanges = ranges;
  }

  private findOption(date: Date): DateRange {
    const result = this.yearMonthOptions.find((option) => {
      return isDateInsideRange(date, option.value.start, option.value.end);
    });
    return result ? result.value : undefined;
  }

  private findEarliestDate(): DateRange {
    return this.yearMonthOptions.length > 0 ? this.yearMonthOptions[this.yearMonthOptions.length - 1].value : undefined;
  }

  // TODO: not implemented
  private findClosestMatch(dateRange: DateRange): DateRange {
    return this.findEarliestDate();
  }


  private calcYearOptions(dateRanges: DateRangeDTO): SelectControlOption<DateRange>[] {
    return Object.keys(dateRanges).map((year) => {
      const startDate: Date = new Date(Date.UTC(+year, 0, 1, 0, 0, 0));
      const endDate: Date = new Date(Date.UTC(+year + 1, 0, 0, 0, 0, 0));
      return {
        option: year,
        value: {start: startDate, end: endDate}
      };
    });
  }

  private calcMonthOptions(dateRanges: DateRangeDTO): SelectControlOption<DateRange>[] {
    return Object.keys(dateRanges).reduce((acc, year) => {
      return dateRanges[year].reduce((acc2, month) => {
        const monthName: string = new Date(+year, month - 1, 1)
          .toLocaleString('de-DE', {month: 'long'});

        const startDate: Date = new Date(Date.UTC(+year, month - 1, 1));
        const endDate: Date = new Date(Date.UTC(+year, month, 0, 23, 59));

        acc2.push({
          option: monthName + ' - ' + year,
          value: {start: startDate, end: endDate}
        });

        return acc2;
      }, acc);
    }, []);
  }

  private calcWeekOptions(dateRanges: DateRangeDTO): SelectControlOption<DateRange>[] {
    const formatDate = (date: Date) => {
      return moment(date).format('DD.MMM YY'); // TODO auf UTC ändern
    };

    return Object.keys(dateRanges).reduce((acc, year) => {
      return dateRanges[year].reduce((acc2, month) => {

        const date = new Date(Date.UTC(+year, month - 1, 1));

        const endOfMonth = moment(date).endOf('month').toDate();

        while (date.getTime() < endOfMonth.getTime()) {

          const startOfWeek = getStartOfDay(getStartOfWeek(date));
          const endOfWeek = getEndOfDay(getEndOfWeek(date));

          const option = {
            option: formatDate(startOfWeek) + ' - ' + formatDate(endOfWeek),
            value: {start: startOfWeek, end: endOfWeek}
          };

          if (acc2.findIndex((o) => {
            return o.value.start.getTime() === option.value.start.getTime()
              && o.value.end.getTime() === option.value.end.getTime();
          }) < 0) {
            acc2.push(option);
          }

          date.setDate(date.getDate() + 7);
        }

        return acc2;
      }, acc);
    }, []);
  }

}
